import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { DefaultOrgIcon } from '~assets/icons'
import { type RootState } from '~redux/rootReducer'
import { selectWorkspaceOrgs } from '~redux/slices/organizationSlice'
import { type EmailIdentity } from '~types'

import Avatar from '../Avatar'
import Chip from '../Chip'
import Dropdown, { type DropdownOption } from '../Dropdown'

export interface OrganizationSelectorProps {
  onSelect?: (org: EmailIdentity) => void
  onMultiSelect?: (orgs: EmailIdentity[]) => void
  preSelectOrg?: string
  orgsOnly?: boolean
  searchable?: boolean
  searchThreshold?: number
  placeholder?: string
  disabled?: boolean
  fullWidth?: boolean
  label?: string
}

const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({
  onSelect,
  onMultiSelect,
  preSelectOrg,
  orgsOnly = false,
  searchable: searchableProp,
  searchThreshold = 10,
  placeholder = 'Select organization',
  disabled = false,
  fullWidth = false,
  label = 'Organization',
}) => {
  const SEARCHABLE_ORGS_THRESHOLD = 8
  const FORCE_SEARCHABLE_THRESHOLD = 15
  const multiple = !!onMultiSelect
  const reduxSelectedOrgId = useSelector(
    (state: RootState) => state?.organization.selectedOrganization?.id,
  )
  const allEmailIdentities = useSelector(selectWorkspaceOrgs)
  const emailIdentities = useMemo(
    () => allEmailIdentities.filter((identity) => !orgsOnly || identity.organization_id),
    [allEmailIdentities, orgsOnly],
  )
  const orgRefreshTrigger = useSelector((state: RootState) => state?.organization.refreshTrigger)

  const initialValue = multiple ? [] : (preSelectOrg ?? reduxSelectedOrgId)

  const [selectedValue, setSelectedValue] = useState<string | string[] | undefined>(initialValue)

  useEffect(() => {
    if (multiple) return
    const id = selectedValue as string | undefined
    const match = emailIdentities.find((i) => i.id === id)
    if (match && onSelect) onSelect(match)
  }, [orgRefreshTrigger])

  useEffect(() => {
    if (multiple) return
    if (!selectedValue && emailIdentities.length > 0) {
      const first = emailIdentities[0]
      setSelectedValue(first.id)
      onSelect?.(first)
    }
  }, [emailIdentities.length])

  const options: DropdownOption[] = useMemo(
    () =>
      emailIdentities.map((org) => {
        const logoSrc = org.image ?? org.workspace_icon
        return {
          value: org.id,
          label: org.name ?? '',
          avatar: logoSrc ? (
            <Avatar size='sm' src={logoSrc} alt={org.name ?? 'Organization'} />
          ) : (
            <DefaultOrgIcon width={20} height={20} />
          ),
        }
      }),
    [emailIdentities],
  )

  const renderSelectedValue = (opt: DropdownOption): React.ReactNode => {
    const org = emailIdentities.find((i) => i.id === opt.value)
    const label = org?.name ?? opt.label
    const logoSrc = org?.image ?? org?.workspace_icon
    return (
      <Chip
        label={label}
        size='small'
        avatar={
          logoSrc ? (
            <Avatar size='sm' src={logoSrc} alt={label} />
          ) : (
            <DefaultOrgIcon width={16} height={16} />
          )
        }
        onDelete={(e) => {
          e.stopPropagation()
          const current = (selectedValue as string[]) ?? []
          const next = current.filter((id) => id !== opt.value)
          setSelectedValue(next)
          const orgs = next
            .map((id) => emailIdentities.find((i) => i.id === id))
            .filter(Boolean) as EmailIdentity[]
          onMultiSelect?.(orgs)
        }}
      />
    )
  }

  const handleChange = (value: string | string[]): void => {
    setSelectedValue(value)
    if (multiple) {
      const ids = value as string[]
      const orgs = ids
        .map((id) => emailIdentities.find((i) => i.id === id))
        .filter(Boolean) as EmailIdentity[]
      onMultiSelect?.(orgs)
    } else {
      const id = Array.isArray(value) ? value[0] : value
      const org = emailIdentities.find((i) => i.id === id)
      if (org) onSelect?.(org)
    }
  }

  return (
    <Dropdown
      label={label}
      options={options}
      value={selectedValue}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      searchable={
        emailIdentities.length >= FORCE_SEARCHABLE_THRESHOLD ||
        (!!searchableProp && emailIdentities.length >= SEARCHABLE_ORGS_THRESHOLD)
      }
      searchThreshold={searchThreshold}
      fullWidth={fullWidth}
      multiple={multiple}
      renderValue={multiple ? renderSelectedValue : undefined}
      containerSx={fullWidth ? undefined : { minWidth: 300 }}
    />
  )
}

export default OrganizationSelector
